import React, { useState } from "react";
import axios from "axios";
import { X, Send, Loader2, ChevronLeft, Building2, MapPin, Home, Users2 } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { JOB_TYPES, jobTypeLabel } from "../config/jobTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same 5-currency multi-country set the job-posting form already uses — never
// hardcoded to USD/EUR, matches InfoCityJobs' existing currency system.
const CURRENCIES = [
  { code: "EUR", symbol: "€" }, { code: "USD", symbol: "US$" },
  { code: "UYU", symbol: "$U" }, { code: "ARS", symbol: "AR$" }, { code: "BRL", symbol: "R$" },
];
const PERIODS = ["hour", "day", "week", "month", "year"];
const WORK_MODES = [
  { id: "onsite", icon: Building2 }, { id: "hybrid", icon: Users2 }, { id: "remote", icon: Home },
];

function FieldBlock({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="flex items-start gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 min-h-[18px]">
        <span className="leading-tight">{label}</span>
      </label>
      {children}
    </div>
  );
}

const fieldInputClass = "w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]";

export default function OfferFormModal({ application, job, onClose, onSent }) {
  const { t } = useLanguage();
  const [step, setStep] = useState("form");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    job_title: application.job_title || job?.title || "",
    work_mode: job?.work_mode || "onsite",
    employment_type: job?.job_type || "full-time",
    schedule: "",
    start_date: "",
    location: job?.location || "",
    additional_details: "",
    min_amount: job?.salary_min != null ? String(job.salary_min) : "",
    max_amount: job?.salary_max != null ? String(job.salary_max) : "",
    currency: job?.salary_currency || "USD",
    period: job?.salary_period || "month",
    comp_details: "",
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const body = {
        job_title: form.job_title,
        work_mode: form.work_mode,
        employment_type: form.employment_type,
        schedule: form.schedule,
        start_date: form.start_date,
        location: form.location,
        additional_details: form.additional_details,
        compensation: {
          min_amount: form.min_amount !== "" ? parseFloat(form.min_amount) : null,
          max_amount: form.max_amount !== "" ? parseFloat(form.max_amount) : null,
          currency: form.currency || null,
          period: form.period || null,
          details: form.comp_details,
        },
      };
      const res = await axios.post(`${API}/applications/${application.application_id}/offer`, body, { headers: getAuthHeaders() });
      onSent(res.data.offer);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || t("offerModal.sendError"));
    } finally {
      setSending(false);
    }
  };

  const compensationLine = () => {
    const parts = [];
    if (form.min_amount) {
      const symbol = CURRENCIES.find((c) => c.code === form.currency)?.symbol || form.currency;
      let amount = `${symbol} ${form.min_amount}`;
      if (form.max_amount && form.max_amount !== form.min_amount) amount += `–${form.max_amount}`;
      if (form.period) amount += ` / ${t(`offerModal.period_${form.period}`)}`;
      parts.push(amount);
    }
    return parts.join(" ");
  };

  const canContinue = !!form.job_title.trim();

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center" data-testid="offer-form-modal" onClick={onClose}>
      <div className="bg-white w-full max-h-[92dvh] rounded-t-2xl sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {step === "preview" && (
              <button onClick={() => setStep("form")} data-testid="offer-form-back" className="p-1 -ml-1 rounded-lg text-slate-400 hover:bg-slate-100 flex-shrink-0">
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h3 className="font-poppins font-bold text-base text-slate-900 truncate" data-testid="offer-form-title">
              {step === "form" ? t("offerModal.title") : t("offerModal.previewTitle")}
            </h3>
          </div>
          <button onClick={onClose} data-testid="offer-form-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {step === "form" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FieldBlock label={t("offerModal.jobTitle")} className="sm:col-span-2">
                <input type="text" data-testid="offer-job-title-input" value={form.job_title} onChange={(e) => set({ job_title: e.target.value })} className={fieldInputClass} />
              </FieldBlock>

              <FieldBlock label={t("offerModal.workMode")} className="sm:col-span-2">
                <div className="grid grid-cols-3 gap-2">
                  {WORK_MODES.map(({ id, icon: Icon }) => (
                    <button key={id} type="button" data-testid={`offer-work-mode-${id}`}
                      onClick={() => set({ work_mode: id })}
                      className={`flex flex-col items-center justify-center gap-1 h-[60px] rounded-xl border-2 text-xs font-semibold transition-colors ${form.work_mode === id ? "border-[#028CF8] text-[#028CF8] bg-[#028CF8]/5" : "border-slate-200 text-slate-500"}`}>
                      <Icon className="h-4 w-4 flex-shrink-0" /> {t(`postJobLabels.workMode${id.charAt(0).toUpperCase() + id.slice(1)}`)}
                    </button>
                  ))}
                </div>
              </FieldBlock>

              <FieldBlock label={t("offerModal.compMin")}>
                <input type="number" data-testid="offer-comp-min-input" value={form.min_amount} onChange={(e) => set({ min_amount: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("offerModal.compMax")}>
                <input type="number" data-testid="offer-comp-max-input" value={form.max_amount} onChange={(e) => set({ max_amount: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("offerModal.currency")}>
                <select data-testid="offer-currency-select" value={form.currency} onChange={(e) => set({ currency: e.target.value })} className={fieldInputClass}>
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} — {t(`geo.currencyNames.${c.code}`)}</option>)}
                </select>
              </FieldBlock>
              <FieldBlock label={t("offerModal.period")}>
                <select data-testid="offer-period-select" value={form.period} onChange={(e) => set({ period: e.target.value })} className={fieldInputClass}>
                  {PERIODS.map((p) => <option key={p} value={p}>{t(`offerModal.period_${p}`)}</option>)}
                </select>
              </FieldBlock>
              <FieldBlock label={t("offerModal.compDetails")} className="sm:col-span-2">
                <input type="text" data-testid="offer-comp-details-input" value={form.comp_details} onChange={(e) => set({ comp_details: e.target.value })}
                  placeholder={t("offerModal.compDetailsPlaceholder")} className={fieldInputClass} />
              </FieldBlock>

              <FieldBlock label={t("offerModal.employmentType")}>
                <select data-testid="offer-employment-type-select" value={form.employment_type} onChange={(e) => set({ employment_type: e.target.value })} className={fieldInputClass}>
                  {JOB_TYPES.map((jt) => <option key={jt} value={jt}>{jobTypeLabel(jt, t)}</option>)}
                </select>
              </FieldBlock>
              <FieldBlock label={t("offerModal.schedule")}>
                <input type="text" data-testid="offer-schedule-input" value={form.schedule} onChange={(e) => set({ schedule: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("offerModal.startDate")}>
                <input type="date" data-testid="offer-start-date-input" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("offerModal.location")}>
                <input type="text" data-testid="offer-location-input" value={form.location} onChange={(e) => set({ location: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("offerModal.additionalDetails")} className="sm:col-span-2">
                <textarea rows={3} data-testid="offer-additional-details-input" value={form.additional_details} onChange={(e) => set({ additional_details: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
              </FieldBlock>
            </div>
          ) : (
            <div className="space-y-3" data-testid="offer-preview">
              <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-wide">{t("offerModal.previewSubtitle")}</p>
              <div className="rounded-xl border border-slate-200 p-4 space-y-2.5 text-sm">
                <p><span className="font-semibold text-slate-500">{t("offerModal.company")}:</span> {job?.company_name || application.company_name}</p>
                <p><span className="font-semibold text-slate-500">{t("offerModal.jobTitle")}:</span> {form.job_title}</p>
                {compensationLine() && <p><span className="font-semibold text-slate-500">{t("offerModal.compMin")}:</span> {compensationLine()}</p>}
                {form.comp_details && <p><span className="font-semibold text-slate-500">{t("offerModal.compDetails")}:</span> {form.comp_details}</p>}
                <p className="flex items-center gap-1.5"><span className="font-semibold text-slate-500">{t("offerModal.workMode")}:</span> {t(`postJobLabels.workMode${form.work_mode.charAt(0).toUpperCase() + form.work_mode.slice(1)}`)}</p>
                <p><span className="font-semibold text-slate-500">{t("offerModal.employmentType")}:</span> {jobTypeLabel(form.employment_type, t)}</p>
                {form.schedule && <p><span className="font-semibold text-slate-500">{t("offerModal.schedule")}:</span> {form.schedule}</p>}
                {form.start_date && <p><span className="font-semibold text-slate-500">{t("offerModal.startDate")}:</span> {form.start_date}</p>}
                {form.location && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {form.location}</p>}
                {form.additional_details && <p><span className="font-semibold text-slate-500">{t("offerModal.additionalDetails")}:</span> {form.additional_details}</p>}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600" data-testid="offer-form-error">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} data-testid="offer-form-cancel" className="h-11 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            {t("offerModal.cancel")}
          </button>
          {step === "form" ? (
            <button onClick={() => setStep("preview")} disabled={!canContinue} data-testid="offer-form-continue"
              className="h-11 px-5 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all disabled:opacity-50">
              {t("offerModal.continue")}
            </button>
          ) : (
            <button onClick={send} disabled={sending} data-testid="offer-form-send"
              className="flex items-center gap-2 h-11 px-5 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("offerModal.send")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
